import React, { useEffect, useState } from 'react';
import { ArrowLeft, Download, Smartphone, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const APK_URL = '/apk/motoristas-vip-litoral.apk';
const APK_MARKER = '/apk/apk-available.json';

const DownloadAppPage = () => {
  const navigate = useNavigate();
  const [apkExists, setApkExists] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Check the marker file (a small JSON) that only exists when the APK is hosted.
    // We can't rely on HEAD /apk/*.apk because the SPA catch-all returns index.html.
    fetch(APK_MARKER, { cache: 'no-store' })
      .then((r) => r.ok ? r.json() : null)
      .then((d) => setApkExists(!!(d && d.available)))
      .catch(() => setApkExists(false))
      .finally(() => setChecking(false));
  }, []);

  const isAndroid = /android/i.test(navigator.userAgent || '');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent || '') && !window.MSStream;

  return (
    <div className="mobile-shell">
      <div className="px-6 pt-6 pb-24">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[var(--bj-text)] font-semibold mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft size={18} /> Voltar
        </button>

        {/* Hero */}
        <div
          className="rounded-2xl p-5 text-white mb-4"
          style={{ background: 'linear-gradient(135deg, #071e42 0%, #0B2A5B 45%, #123b7a 100%)' }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{ background: 'linear-gradient(135deg, #F5C518, #E8B800)' }}
            >
              <Smartphone size={24} className="text-[var(--bj-navy)]" />
            </div>
            <div>
              <div className="text-lg font-extrabold">Baixar App Android</div>
              <div className="text-xs text-white/70">Motoristas VIP Litoral</div>
            </div>
          </div>
          <p className="text-sm text-white/90 mt-2">
            Aplicativo Android oficial. Instalação com <b>1 clique</b>, sem passar por menu do navegador.
          </p>
        </div>

        {/* Download button */}
        {checking ? (
          <div className="text-center text-[var(--bj-muted)] py-6">Verificando…</div>
        ) : apkExists ? (
          <a
            href={APK_URL}
            download="Motoristas-VIP-Litoral.apk"
            className="w-full flex items-center justify-center gap-2 rounded-full font-extrabold py-4 text-base transition-all hover:scale-[1.02] active:scale-95"
            style={{
              background: 'linear-gradient(135deg, #E8B800 0%, #F5C518 50%, #E8B800 100%)',
              color: '#0B2A5B',
              border: '2px solid #B48200',
              boxShadow: '0 4px 14px rgba(180,130,0,0.35)',
            }}
            data-testid="download-apk-btn"
          >
            <Download size={20} strokeWidth={2.5} /> Baixar APK Android
          </a>
        ) : (
          <div className="rounded-2xl p-5 border-2 border-dashed border-[var(--bj-muted)] bg-white text-center">
            <div className="text-sm text-[var(--bj-text)] font-semibold">APK em preparação</div>
            <div className="text-xs text-[var(--bj-muted)] mt-1">
              O aplicativo Android será disponibilizado em breve. Enquanto isso, use o botão "Instalar app" na tela inicial.
            </div>
          </div>
        )}

        {/* Instructions */}
        {isAndroid && apkExists && (
          <div className="mt-5 bg-white rounded-2xl p-4 border border-[var(--bj-border)]">
            <div className="font-extrabold text-[var(--bj-text)] mb-2 flex items-center gap-2">
              <ShieldCheck size={16} className="text-green-600" /> Como instalar no Android
            </div>
            <ol className="text-sm text-[var(--bj-text)] space-y-2 pl-5 list-decimal">
              <li>Toque em <b>Baixar APK Android</b> acima.</li>
              <li>Confirme "Baixar assim mesmo" se o Chrome perguntar.</li>
              <li>Abra o arquivo baixado — vai aparecer <b>"Instalar"</b>.</li>
              <li>Se pedir permissão, ative "Permitir desta fonte" (só nesta vez).</li>
              <li>Pronto! Ícone aparece na tela inicial.</li>
            </ol>
          </div>
        )}

        {isIOS && (
          <div className="mt-5 bg-white rounded-2xl p-4 border border-[var(--bj-border)]">
            <div className="font-extrabold text-[var(--bj-text)] mb-2">📱 iPhone / iPad</div>
            <div className="text-sm text-[var(--bj-text)]">
              O iPhone não permite instalação de APK. Volte à tela inicial e toque em <b>Instalar app</b> —
              o Safari te guia para adicionar à tela inicial em 2 toques.
            </div>
          </div>
        )}

        {/* Features */}
        <div className="mt-5 space-y-2">
          {[
            'Sem passar pelos ⋮ 3 pontinhos do navegador',
            'Abre em tela cheia como app nativo',
            'Ícone dourado na área de trabalho',
            'Notificações e login salvos',
          ].map((f) => (
            <div key={f} className="flex items-center gap-2 text-sm text-[var(--bj-text)]">
              <CheckCircle2 size={16} className="text-green-600 shrink-0" /> {f}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DownloadAppPage;
